/* eslint-disable camelcase */
import { FastField, Formik } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import * as React from "react"
import { useRef } from "react"
import { Appearance, TextInput, ViewStyle } from "react-native"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { FormControl } from "../../form-control/form-control"
import { FormProps } from "../form"
import { PasswordStrengthMeterBar } from "../../password-strength-meter-bar/password-strength-meter-bar"

const FORM_CONTROL: ViewStyle = {
  marginBottom: 4
}

export interface ChangePasswordFormProps extends FormProps {
  initialValues?: {
    current_password?: string,
    password?: string,
    password_confirmation?: string
  }
}

/**
 * Describe your component here
 */
export const ChangePasswordForm = observer(function ChangePasswordForm(props: ChangePasswordFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props
  const input = {
    current_password: useRef<TextInput>(),
    password: useRef<TextInput>(),
    password_confirmation: useRef<TextInput>(),
  }
  const nextInput = {
    current_password: () => input.password.current.focus(),
    password: () => input.password_confirmation.current.focus(),
  }
  const validationSchema = yup.object({
    current_password: yup.string().ascii().min(6, translate("minPassword")).required(translate("validationInput")).label(translate("changePasswordForm.currentPassword")),
    password: yup.string().ascii().zxcvbn(1).min(6, translate("minPassword")).required(translate("validationInput")).label(translate("changePasswordForm.password")),
    password_confirmation: yup.string().ascii()
      .oneOf([yup.ref('password')], translate("changePasswordForm.passwordConfirmationDoesntMatch"))
      .required(translate("validationInput")).label(translate("changePasswordForm.passwordConfirmation"))
  })

  return (
    <Formik
      initialValues={{
        current_pasword: '',
        password: '',
        password_confirmation: '',
        ...initialValues
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ isValid, handleSubmit }) => (
        <>
          <FastField name="current_password">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`changePasswordForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  type: "password",
                  autoCapitalize: "none",
                  autoComplete: "password",
                  autoCorrect: false,
                  textContentType: "newPassword",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `changePasswordForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FastField name="password">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`changePasswordForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  type: "password",
                  autoCapitalize: "none",
                  autoComplete: "password",
                  autoCorrect: false,
                  textContentType: "newPassword",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `changePasswordForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                helperTextComponent={<PasswordStrengthMeterBar colorScheme={colorScheme} value={field.value} />}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FastField name="password_confirmation">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`changePasswordForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  type: "password",
                  autoCapitalize: "none",
                  autoComplete: "password",
                  autoCorrect: false,
                  textContentType: "password",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `changePasswordForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
        </>
      )}
    </Formik>
  )
})

ChangePasswordForm.displayName = 'ChangePasswordForm'
